import { SKATE_CREW_KIND, type SkateCrew } from '@/lib/types';
import type { CordnEndpoint } from './types';

/** Default ContextVM relay a coordinator listens on when only an https URL is given. */
const DEFAULT_CVM_RELAY = 'wss://relay.contextvm.org';

/**
 * Resolve a crew's `coordinator` config into a usable {@link CordnEndpoint}.
 *
 * Rules:
 *  - `wss://` / `ws://` URL → used directly as the coordinator's relay.
 *  - `https://` URL         → the coordinator is addressed over the default
 *                             ContextVM relay (messages are routed by the
 *                             coordinator pubkey); the https URL remains the
 *                             human/ops endpoint. We still need a relay to move
 *                             ciphertext, so we fall back to the CVM relay.
 *
 * Returns null when the crew has no coordinator configured.
 */
export function resolveCordnEndpoint(crew: SkateCrew): CordnEndpoint | null {
  const coord = crew.coordinator;
  if (!coord?.url) return null;

  const groupId = `${SKATE_CREW_KIND}:${crew.pubkey}:${crew.identifier}`;
  let relayUrl: string;

  try {
    const u = new URL(coord.url);
    if (u.protocol === 'wss:' || u.protocol === 'ws:') {
      relayUrl = u.href;
    } else if (u.protocol === 'https:' || u.protocol === 'http:') {
      // Coordinator exposed over http(s): route ciphertext via the CVM relay.
      relayUrl = DEFAULT_CVM_RELAY;
    } else {
      return null;
    }
  } catch {
    return null;
  }

  return {
    relayUrl,
    coordinatorPubkey: coord.pubkey,
    groupId,
  };
}
